/* eslint-disable react/forbid-prop-types */
import React, {
  useState, useRef, useEffect, useImperativeHandle, forwardRef,
} from 'react';
import {
  func, bool, number, string, oneOfType, shape,
} from 'prop-types';
import moment from 'moment';
import update from 'immutability-helper';
import {
  pipe, replace, min, max,
} from 'ramda';
import { DatePickerField } from '@hyphengroup/cag-components';

import './DateInput.scss';

const FORMAT = 'YYYY-MM-DD';

const DateInput = forwardRef(
  ({
    id,
    defaultValue,
    value: controlledValue,
    format,
    yearTemplates,
    disabled,
    dataSelectorId,
    status,
    errorMessage,
    label,
    type,
    maxDate,
    minDate,
    ...props
  }, ref) => {
    /* Refs */
    const isModeControlledRef = useRef(
      (function() {
        const modeControlled = controlledValue !== undefined;
        const modeUncontrolled = defaultValue !== undefined;
        if (modeControlled && modeUncontrolled) {
          // eslint-disable-next-line
          console.error(`Warning: You shoud not specify both 'value' and 'defaultValue' at the same time.\n \
      To use controlled mode specify 'value' only,\n \
      To use uncontrolled mode you can optionally specify 'defaultValue'`);
        }
        return modeControlled;
      })()
    );

    const dayInputRef = useRef();
    const monthInputRef = useRef();
    const yearInputRef = useRef();

    const dayDisplay2DigitsRef = useRef(true);
    const monthDisplay2DigitsRef = useRef(true);

    const isControlActiveRef = useRef(false);
    const changeInputControlRef = useRef();
    const hotValueRef = useRef();

    /* Helpers */
    const dissect = pipe(
      v => moment(v, format),
      date => ({
        day: date?.date() || '',
        month: date?.month() + 1 || '',
        year: date?.year() || '',
      })
    );

    const assemble = ({ day, month, year }) => (
      isModeControlledRef.current
        ? ((function() {
          // const dayString = dayInputRef.current?.value;
          // const monthString = monthInputRef.current?.value;
          // const yearString = yearInputRef.current?.value;

          return format
            .replace('DD', day)
            .replace('MM', month)
            .replace('YYYY', year);
        })())
        : ((function() {
          // const isDayValid = day >= 1 && day <= 31;
          // const isMonthValid = month >= 1 && month <= 12;
          // const isYearValid = year >= 1000;
          // const date = moment({ day, month: month - 1, year });
          // isDayValid && isMonthValid && isYearValid && date.isValid() ? date.format(format) : '';
          const dayComplete = day > 0 || dayInputRef.current?.value.length >= 2;
          const monthComplete = month > 0 || monthInputRef.current?.value.length >= 2;
          const yearComplete = year > 999 || yearInputRef.current?.value.length >= 4;

          const dayString = `${day}`.padStart(2, '0');
          const monthString = `${month}`.padStart(2, '0');
          const yearString = `${year}`.padStart(4, '0');

          return dayComplete && monthComplete && yearComplete
            ? format
              .replace('DD', dayString)
              .replace('MM', monthString)
              .replace('YYYY', yearString)
            : '';
        })()));

    /* State */
    const [uncontrolledValue, setUncontrolledValue] = useState(() => (
      isModeControlledRef.current ? {} : dissect(defaultValue)
    ));
    const value = isModeControlledRef.current ? dissect(controlledValue) : uncontrolledValue;

    // eslint-disable-next-line
    const [metas, setMetas] = useState({
      day: {
        active: false,
        touched: false,
      },
      month: {
        active: false,
        touched: false,
      },
      year: {
        active: false,
        touched: false,
      },
    });

    /* Expose */
    const valueRef = useRef(value);
    useEffect(() => {
      valueRef.current = value;
    });
    useImperativeHandle(ref, () => ({
      get value() {
        return assemble(valueRef.current);
      },
    }));

    /* Handlers */
    const handleKeyDown = ({ target: { name }, key }) => {
      if (key === 'Tab') {
        /* NOTE:
          - Tab will move to next input control by itself.
          - Tab will 'exit' the control if current input control is 'year'.
        */
        name !== 'year' && (isControlActiveRef.current = true);
        return;
      }
      let currInputEl;
      let prevInputEl;
      let nextInputEl;
      name === 'day'
        && (currInputEl = dayInputRef.current)
        && (nextInputEl = monthInputRef.current);
      name === 'month'
        && (currInputEl = monthInputRef.current)
        && (prevInputEl = dayInputRef.current)
        && (nextInputEl = yearInputRef.current);
      name === 'year'
        && (currInputEl = yearInputRef.current)
        && (prevInputEl = monthInputRef.current)
        && (nextInputEl = dayInputRef.current);

      // Reset value of input control for day & year if already have 2 digits
      name !== 'year' && currInputEl.value?.length >= 2
        && /\d/.test(key)
        && (currInputEl.value = '');

      const moveToPrevInputControl = name !== 'day' && ['Backspace'].includes(key) && currInputEl.value === '';
      const moveToNextInputControl = ['/'].includes(key) || ((function() {
        let next;
        switch (name) {
          case 'day':
            next = currInputEl.value === '' ? parseInt(key, 10) > 3 : Number.isInteger(parseInt(key, 10));
            break;
          case 'month':
            next = currInputEl.value === '' ? parseInt(key, 10) > 1 : Number.isInteger(parseInt(key, 10));
            break;
          default:
            next = false;
            break;
        }
        return next;
      })());
      moveToPrevInputControl || moveToNextInputControl
        ? (isControlActiveRef.current = true)
        : (changeInputControlRef.current = null);
      moveToPrevInputControl && (changeInputControlRef.current = () => prevInputEl.focus());
      moveToNextInputControl && (changeInputControlRef.current = () => nextInputEl.focus());

      /* NOTE: When to change input control
        - Immediately if key pressed is control key since handleChange won't be called
        - In handleChange, i.e. updating value correctly, then change control
      */
      if (key === 'Backspace') {
        name === 'day' && (dayDisplay2DigitsRef.current = false);
        name === 'month' && (monthDisplay2DigitsRef.current = false);
        moveToPrevInputControl && name === 'year' && (monthDisplay2DigitsRef.current = false);
        moveToPrevInputControl && name === 'month' && (dayDisplay2DigitsRef.current = false);
        changeInputControlRef.current?.();
      } else {
        name === 'day' && (dayDisplay2DigitsRef.current = currInputEl.value === '0');
        name === 'month' && (monthDisplay2DigitsRef.current = currInputEl.value === '0');
      }
    };

    const handleFocus = ({ target: { name } }) => {
      setMetas(prevMetas => update(prevMetas, { [name]: { active: { $set: true } } }));

      const { onFocus } = props;
      !isControlActiveRef.current && onFocus?.(assemble(value));
      isControlActiveRef.current = false;
    };

    const handleChange = ({ target: { name, value: valuePartString } }) => {
      // Prevent empty current input control when click 'Tab' and then '/'
      if (['/'].includes(valuePartString)) {
        changeInputControlRef.current?.();
        return;
      }

      const coercedValuePartString = pipe(
        replace(/[^0-9]/g, ''),
        v => v.substring(0, name === 'year' ? 4 : 2),
      )(valuePartString);
      const normalizedValuePart = parseInt(coercedValuePartString, 10);
      const normalizedValue = { ...value, [name]: normalizedValuePart };

      !isModeControlledRef.current && setUncontrolledValue(normalizedValue);
      const { onChange } = props;
      assemble(value) !== assemble(normalizedValue) && onChange?.(assemble(normalizedValue));

      // This assures that handleBlur binds to the latest 'value' after a re-render
      // setTimeout(() => changeInputControlRef.current?.(), 0);
      // This is better approach to prevent flickering
      hotValueRef.current = normalizedValue;
      changeInputControlRef.current?.();
    };

    const handleBlur = ({ target: { name } }) => {
      setMetas(prevMetas => update(prevMetas, {
        [name]: { $set: { active: false, touched: true } },
      }));

      name === 'day' && (dayDisplay2DigitsRef.current = true);
      name === 'month' && (monthDisplay2DigitsRef.current = true);

      const latestValue = hotValueRef.current || value;
      hotValueRef.current = null;
      const day = name === 'day' && Number.isInteger(latestValue.day)
        ? pipe(max(1), min(31))(latestValue.day)
        : latestValue.day;
      const month = name === 'month' && Number.isInteger(latestValue.month)
        ? pipe(max(1), min(12))(latestValue.month)
        : latestValue.month;
      const year = name === 'year' && Number.isInteger(latestValue.year)
        ? ((function() {
          const currYearArr = `${latestValue.year !== null ? latestValue.year : ''}`.split('').reverse();
          const defaultYearArr = ((function() {
            const defaultYearTemplate = currYearArr?.join('');
            let yearTemplate;
            switch (currYearArr.length) {
              case 1: { yearTemplate = yearTemplates?.one || defaultYearTemplate; break; }
              case 2: { yearTemplate = yearTemplates?.two || defaultYearTemplate; break; }
              case 3: { yearTemplate = yearTemplates.three || defaultYearTemplate; break; }
              default: { yearTemplate = defaultYearTemplate; }
            }
            return yearTemplate;
          })()).split('').reverse();
          const finalYearArr = defaultYearArr.map((v, i) => currYearArr[i] || v).reverse().join('');
          return latestValue.year !== null && parseInt(finalYearArr, 10);
        })())
        : latestValue.year;

      const finalValue = { day, month, year };
      !isModeControlledRef.current && setUncontrolledValue(finalValue);
      !isControlActiveRef.current && ((function() {
        const { onChange, onBlur } = props;
        /* NOTE:
          Input need to know if the value has changed
          since it ignores the value reported via onBlur
        */
        assemble(value) !== assemble(finalValue) && onChange?.(assemble(finalValue));
        return onBlur?.(assemble(finalValue));
      })());
    };

    return (
      type === 'datePicker' ? (
        <DatePickerField
          id={id}
          dataSelectorId={dataSelectorId}
          name="datePicker"
          base="classic"
          status={status}
          defaultValue={new Date(defaultValue)}
          errorMessage={errorMessage}
          label={label}
          withoutValidation
          validatedValue={value}
          onChange={handleChange}
          onBlur={handleBlur}
          maxDate={maxDate}
          minDate={minDate}
          disabled={disabled}
        />
      )
        : (
          <div className="date-panel-wrapper" data-selector={dataSelectorId}>
            <div className="date-panel">
              <div className="date-panel-input-group">
                <input
                  id={!value.day || (value.day && value.month && value.year) ? id : null}
                  data-selector={`${dataSelectorId}-day`}
                  ref={dayInputRef}
                  name="day"
                  type="tel"
                  placeholder="DD"
                  disabled={disabled}
              /* eslint-disable */
              value={Number.isInteger(value.day)
                ? dayDisplay2DigitsRef.current
                  ? `${value.day}`.padStart(2, '0')
                  : value.day
                : ''}
              /* eslint-enable */
                  onKeyDown={handleKeyDown}
                  onFocus={handleFocus}
                  onChange={handleChange}
                  onBlur={handleBlur}
              /* COMPATIBILITY:
                Prevent the behavior of the wrapper label in Input,
                i.e. blur this control & focus on the control
                whose id is set in htmlFor
              */
                  onClick={e => e.preventDefault()}
                  autoComplete="off"
                />
                <span className="slash">/</span>
                <input
                  id={value.day && !value.month ? id : null}
                  data-selector={`${dataSelectorId}-month`}
                  ref={monthInputRef}
                  name="month"
                  type="tel"
                  placeholder="MM"
                  disabled={disabled}
              /* eslint-disable */
              value={Number.isInteger(value.month)
                ? monthDisplay2DigitsRef.current
                  ? `${value.month}`.padStart(2, '0')
                  : value.month
                : ''}
              /* eslint-enable */
                  onKeyDown={handleKeyDown}
                  onFocus={handleFocus}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  onClick={e => e.preventDefault()}
                  autoComplete="off"
                />
                <span className="slash">/</span>
                <input
                  id={value.day && value.month && !value.year ? id : null}
                  data-selector={`${dataSelectorId}-year`}
                  ref={yearInputRef}
                  name="year"
                  placeholder="YYYY"
                  className="year"
                  type="tel"
                  disabled={disabled}
                  value={Number.isInteger(value.year) ? value.year : ''}
                  onKeyDown={handleKeyDown}
                  onFocus={handleFocus}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  onClick={e => e.preventDefault()}
                  autoComplete="off"
                />
              </div>
            </div>
          </div>
        )
    );
  }
);

DateInput.displayName = 'DateInput';

DateInput.defaultProps = {
  format: FORMAT,
  disabled: false,
  dataSelectorId: '',
};

DateInput.propTypes = {
  id: oneOfType([number, string]),
  defaultValue: string,
  value: string,
  onFocus: func,
  onChange: func,
  onBlur: func,
  format: string,
  yearTemplates: shape({
    one: string,
    two: string,
    three: string,
  }),
  disabled: bool,
  dataSelectorId: string,
  status: string,
  errorMessage: string,
  label: string,
  type: string,
  maxDate: string,
  minDate: string,
};

export default DateInput;
